import { Text, WasabiButton } from '@kk/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { Status } from '@/components/bits';
import { placeholders } from '@/copy/placeholders';
import { useBilling, useBillingRedirect, useErrorMessage } from '@/data/hooks';

/**
 * Why a club's join codes don't work yet, or `null` when they do (DECISIONS 91: while billing is open, a club with no
 * billing, or a paused one, cannot take on families, and a parent who types its code is told the code doesn't work).
 * `billing.canGrow` is the server's own answer, so the screen never works it out for itself.
 */
export function useCodesOnHold(): 'setUp' | 'paused' | null {
  const billing = useBilling().data;
  if (!billing?.enabled || billing.canGrow) return null;
  return billing.status === 'paused' ? 'paused' : 'setUp';
}

/**
 * **THE LINE AND THE BUTTON UNDER A DIMMED JOIN CODE** (the 23 Oct audit, T1-1; DECISIONS 93.2: shown dimmed with
 * why, never hidden, so a director learns the code and can't hand out a dead one by mistake).
 *
 * The button is the billing banner's own: Set Up Billing goes to Settings' Billing section; a paused club goes to
 * Stripe's page, as the banner's Manage Billing does. `onSetUp` lets Settings scroll to its own section instead.
 */
export function CodeOnHoldNote({
  reason,
  onSetUp,
  withoutLine,
  testID,
}: {
  reason: 'setUp' | 'paused';
  onSetUp?: () => void;
  /** A group with no code at all: the line speaks of "this code", so only the button is drawn. */
  withoutLine?: boolean;
  testID?: string;
}) {
  const navigate = useNavigate();
  const redirect = useBillingRedirect();
  const errorMessage = useErrorMessage();
  const [error, setError] = useState<string | null>(null);
  const copy = placeholders.billing;
  const manage = async () => {
    setError(null);
    try {
      await redirect.mutateAsync('manage');
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  return (
    <div className="code-on-hold" data-testid={testID}>
      {withoutLine ? null : (
        <Text variant="bodySm" tone="ink" testID={testID ? `${testID}-line` : undefined}>
          {reason === 'paused' ? copy.codePaused : copy.codeNeedsBilling}
        </Text>
      )}
      {error ? <Status>{error}</Status> : null}
      <div className="actions">
        {reason === 'setUp' ? (
          <WasabiButton
            label={copy.setUpButton}
            size="sm"
            fullWidth={false}
            onPress={onSetUp ?? (() => navigate('/settings'))}
            testID={testID ? `${testID}-setup` : undefined}
          />
        ) : (
          <WasabiButton
            label={copy.manage}
            size="sm"
            fullWidth={false}
            onPress={() => void manage()}
            testID={testID ? `${testID}-manage` : undefined}
          />
        )}
      </div>
    </div>
  );
}
